import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef
} from 'react'
import { gsap, ScrollTrigger, prefersReducedMotion } from '@/lib/gsap'

type ScrollTarget = string | number | HTMLElement

interface ScrollOptions {
    immediate?: boolean
}

interface SmoothScrollApi {
    scrollTo: (target: ScrollTarget, options?: ScrollOptions) => void
    stop: () => void
    start: () => void
}

interface SmoothScrollProps {
    children: ReactNode
    startStopped?: boolean
}

const noop = (): void => {}

const SmoothScrollContext = createContext<SmoothScrollApi>({
    scrollTo: noop,
    stop: noop,
    start: noop
})

export const useSmoothScroll = (): SmoothScrollApi => useContext(SmoothScrollContext)

const resolveTop = (target: ScrollTarget): number | null => {
    if (typeof target === 'number') return target
    const element = typeof target === 'string' ? document.querySelector(target) : target
    if (!element) return null
    return element.getBoundingClientRect().top + window.scrollY
}

export const SmoothScroll: FC<SmoothScrollProps> = ({ children, startStopped = false }): ReactNode => {
    const lenisRef = useRef<Lenis | null>(null)
    const startedRef = useRef(!startStopped)

    useEffect(() => {
        // The journey always starts at the cabin window
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual'
        }
        window.scrollTo(0, 0)

        ScrollTrigger.config({ ignoreMobileResize: true })

        // Hold the page still until whoever asked for it calls start()
        if (!startedRef.current) {
            document.documentElement.style.overflow = 'hidden'
        }

        if (prefersReducedMotion()) return

        const lenis = new Lenis({
            duration: 1.25,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
            wheelMultiplier: 0.9
        })

        lenisRef.current = lenis
        if (!startedRef.current) lenis.stop()
        lenis.on('scroll', ScrollTrigger.update)

        const tick = (time: number): void => {
            lenis.raf(time * 1000)
        }

        gsap.ticker.add(tick)
        gsap.ticker.lagSmoothing(0)

        return () => {
            gsap.ticker.remove(tick)
            gsap.ticker.lagSmoothing(500, 33)
            lenis.destroy()
            lenisRef.current = null
        }
    }, [])

    const scrollTo = useCallback((target: ScrollTarget, options: ScrollOptions = {}): void => {
        const lenis = lenisRef.current
        const top = resolveTop(target)
        if (top === null) return

        if (lenis) {
            lenis.scrollTo(top, {
                immediate: options.immediate,
                duration: 2.2,
                force: true
            })
            return
        }

        window.scrollTo({ top, behavior: options.immediate || prefersReducedMotion() ? 'auto' : 'smooth' })
    }, [])

    const stop = useCallback((): void => {
        lenisRef.current?.stop()
        document.documentElement.style.overflow = 'hidden'
    }, [])

    const start = useCallback((): void => {
        startedRef.current = true
        document.documentElement.style.overflow = ''
        lenisRef.current?.start()
    }, [])

    const api = useMemo<SmoothScrollApi>(() => ({ scrollTo, stop, start }), [scrollTo, stop, start])

    return (
        <SmoothScrollContext.Provider value={api}>
            {children}
        </SmoothScrollContext.Provider>
    )
}
